import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { CATEGORY_COLORS } from "@/hooks/useMapApi";

// Fix default icon paths broken by webpack — but we use divIcon so it's moot.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function makeIcon(color, label = "") {
  const html = `
    <div style="position: relative; width: 28px; height: 36px;">
      <div style="
        position: absolute; top: 0; left: 0;
        width: 28px; height: 28px; border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${color};
        border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.25);
      "></div>
      <div style="
        position: absolute; top: 5px; left: 5px;
        width: 18px; height: 18px; border-radius: 50%;
        background: white; display: flex; align-items: center; justify-content: center;
        font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 600; color: ${color};
      ">${label}</div>
    </div>`;
  return L.divIcon({
    html,
    className: "echoes-pin",
    iconSize: [28, 36],
    iconAnchor: [14, 32],
    popupAnchor: [0, -28],
  });
}

// Component that lets parent control map view (pan/fly) and exposes invalidate
function MapController({ center, zoom, flyTo, onReady }) {
  const map = useMap();
  useEffect(() => {
    if (onReady) onReady(map);
    // Force redraw — important when map is mounted inside a modal/grid that
    // may not have its size calculated yet on first render.
    const t = setTimeout(() => map.invalidateSize(), 50);
    const t2 = setTimeout(() => map.invalidateSize(), 300);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [map, onReady]);
  useEffect(() => {
    if (flyTo) {
      map.flyTo([flyTo.lat, flyTo.lng], flyTo.zoom || Math.max(map.getZoom(), 13), { duration: 0.8 });
    }
  }, [flyTo, map]);
  useEffect(() => {
    if (center) map.setView([center.lat, center.lng], zoom || map.getZoom());
  }, [center?.lat, center?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Component that reports map move events upward
function MapEvents({ onBoundsChange }) {
  const map = useMap();
  const tRef = useRef(null);
  useEffect(() => {
    if (!onBoundsChange) return;
    const fire = () => {
      clearTimeout(tRef.current);
      tRef.current = setTimeout(() => {
        const b = map.getBounds();
        onBoundsChange({
          south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast(),
          zoom: map.getZoom(),
        });
      }, 350);
    };
    map.on("moveend", fire);
    map.on("zoomend", fire);
    fire();
    return () => { map.off("moveend", fire); map.off("zoomend", fire); };
  }, [map, onBoundsChange]);
  return null;
}

export default function MapView({
  stories = [],
  center = { lat: 39.0, lng: -95.5 },
  zoom = 4,
  flyTo = null,
  onBoundsChange = null,
  onSelectStory = null,
  activeStoryId = null,
  numbered = false,
  cluster = true,
  height = "calc(100vh - 200px)",
  onMapReady = null,
}) {
  const markers = useMemo(() =>
    stories.map((s, i) => {
      const color = CATEGORY_COLORS[s.category] || "#1A2B3C";
      const label = numbered ? String(i + 1) : "";
      return (
        <Marker
          key={s.id}
          position={[s.lat, s.lng]}
          icon={makeIcon(color, label)}
          eventHandlers={{
            click: () => onSelectStory && onSelectStory(s),
          }}
        >
          <Popup>
            <div style={{ minWidth: 200 }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5, color,
                marginBottom: 4,
              }}>{s.category}</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 500, lineHeight: 1.25, marginBottom: 6 }}>{s.title}</div>
              {s.place_name && (
                <div style={{ fontSize: 11, color: "#718096", marginBottom: 6 }}>{s.place_name}</div>
              )}
              <div style={{ fontSize: 11, color: "#718096" }}>by {s.author_name}</div>
              {onSelectStory && (
                <button
                  onClick={(e) => {
                    // Close the Leaflet popup so the detail modal isn't visually blocked
                    e.target.closest(".leaflet-popup")?.querySelector(".leaflet-popup-close-button")?.click();
                    onSelectStory(s, { open: true });
                  }}
                  style={{
                    marginTop: 8, fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10, textTransform: "uppercase", letterSpacing: 1.5,
                    background: color, color: "white", border: "none",
                    padding: "6px 12px", cursor: "pointer", width: "100%",
                  }}
                  data-testid={`map-popup-open-${s.id}`}
                >Open Echoe</button>
              )}
            </div>
          </Popup>
        </Marker>
      );
    }), [stories, numbered, onSelectStory]);

  return (
    <div style={{ height, width: "100%", position: "relative", border: "1px solid #E5E2D9" }}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{ height: "100%", width: "100%", background: "#F4F1E6" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapController center={null} zoom={zoom} flyTo={flyTo} onReady={onMapReady} />
        <MapEvents onBoundsChange={onBoundsChange} />
        {cluster ? (
          <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
            {markers}
          </MarkerClusterGroup>
        ) : (
          markers
        )}
      </MapContainer>
      {activeStoryId && null /* future: highlight ring */}
    </div>
  );
}
